// eslint-disable-next-line @nx/enforce-module-boundaries -- Shared renderer-independent row focus and theme foundation.
import {
  useDataGridRowFocus,
  getDataGridFocusTheme,
  getColumnSortedIndices,
  getDataGridSortIcons,
  EntityCommentDialog,
  type EntityCommentTarget,
  type ColumnSort,
  type DataGridFocusResult,
} from "@ots-frontend/ots-ui-tw";
import {useMantineTheme} from "@mantine/core";
import {getDataGridSeparatorTheme, useOtsColorScheme} from "../../DataGrid/dataGridTheme";
import {
  CompactSelection,
  DataEditorProps,
  DataEditorRef,
  GridCellKind,
  Item as GridItem,
  Item,
} from "@glideapps/glide-data-grid";
import {Dispatch, DispatchEntity, DispatchPatchDTO, ShiftEntity} from "@ots-frontend/ots-data";
import {forwardRef, useCallback, useEffect, useMemo, useState, useRef} from "react";
import {createPortal} from "react-dom";
import {Group} from "@mantine/core";
import {useDataGridColumnLayout} from "../../DataGrid/useDataGridColumnLayout";
import {DataGridColumnPicker} from "../../DataGrid/DataGridColumnPicker";
import {DataGridSeparatorPicker} from "../../DataGrid/DataGridSeparatorPicker";
import {getDataGridDisplayRows, filterDataGridRecordSelection} from "../../DataGrid/dataGridSeparators";
import {useAuth} from "../../../../providers/AuthProvider/AuthProvider";
import {Theme} from "@glideapps/glide-data-grid/dist/ts/common/styles";
import {CellClickedEventArgs} from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-types";
import {useLayer} from "react-laag";
import {EMPTY_ARRAY} from "./useDispatchDataGrid";
import {EntityDataGridProps} from "../../EntityDataGrid/EntityDataGrid";
import {GridColumnWithContent, GridRowContextMenu, isSizedGridColumn, useDataGrid} from "../../DataGrid/useDataGrid";
import {ThemedDataGrid} from "../../DataGrid/ThemedDataGrid";
import {EntityCommentListModal} from "../../../comment/EntityCommentList/EntityCommentList";
import {Highlight} from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-render";
import {DispatchDataGridContextMenu, DispatchDataGridContextMenuItem} from "../menu/DispatchDataGridContextMenu";
import {EntityMutationCallback} from "../../../../hooks/useEntityMutation";
import {LEGACY_DISPATCH_COLUMN_IDS, useDispatchDataGridCols} from "./useDispatchDataGridCols";
import {getDispatchDataGridRowTheme} from "../dispatchGridTheme";
import {DriverShiftContext, getDriverShiftContext} from "../shift-context";

const EMPTY_SHIFT_ARRAY: ShiftEntity[] = [];

export type DispatchDataGridProps = Partial<EntityDataGridProps<DispatchEntity>> & {
  onItemClick?: (item: DispatchDataGridContextMenuItem, data: DispatchEntity[]) => void;
  updateDispatches?: (dispatches: Partial<Dispatch>[], callback: EntityMutationCallback | undefined) => void;
  patchDispatch?: (dispatchId: string, patch: DispatchPatchDTO, callback: EntityMutationCallback | undefined) => void;
  controlsTarget?: HTMLElement | null;
  columnStorageKey?: string;
  onSeparatorModeChange?: (mode: DispatchSeparatorMode) => void;
  separatorMode?: DispatchSeparatorMode;
  shifts?: ShiftEntity[];
  appDttm?: number;
  onCommentCreated?: () => void;
  focusPersonId?: string;
  onFocusResolved?: (result: DataGridFocusResult) => void;
};

export type DispatchSeparatorMode =
  | "NONE"
  | "DRIVER"
  | "PICKUP_SITE"
  | "DELIVERY_SITE"
  | "PICKUP_COMPANY"
  | "DELIVERY_COMPANY"
  | "SCHEDULED_6_HOUR";

export const DISPATCH_SEPARATOR_OPTIONS: {value: DispatchSeparatorMode; label: string}[] = [
  {value: "NONE", label: "Off"},
  {value: "DRIVER", label: "Driver"},
  {value: "PICKUP_SITE", label: "P/U Site"},
  {value: "DELIVERY_SITE", label: "Del. Site"},
  {value: "PICKUP_COMPANY", label: "P/U Company"},
  {value: "DELIVERY_COMPANY", label: "Del. Company"},
  {value: "SCHEDULED_6_HOUR", label: "6 Hour Scheduled Block"},
];

export type DispatchSeparator = {
  value: string;
  displayValue: string;
};

const getScheduledSixHourBlock = (dispatch: DispatchEntity): DispatchSeparator => {
  if (typeof dispatch.scheduleDttm !== "number") {
    return {
      value: "UNSCHEDULED",
      displayValue: "Unscheduled",
    };
  }

  const date = new Date(dispatch.scheduleDttm);
  const blockStartHour = Math.floor(date.getHours() / 6) * 6;
  const blockEndHour = blockStartHour + 6;
  const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}T${String(blockStartHour).padStart(2, "0")}`;

  return {
    value: value,
    displayValue: `${blockStartHour}:00 - ${blockEndHour}:00`,
  };
};

export const getDispatchSeparator = (
  dispatch: DispatchEntity | undefined,
  separatorMode: DispatchSeparatorMode
): DispatchSeparator | undefined => {
  if (!dispatch || separatorMode === "NONE") {
    return undefined;
  }

  switch (separatorMode) {
    case "DRIVER":
      return {
        value: dispatch.personId ?? "UNASSIGNED",
        displayValue: dispatch.personName ?? "Unassigned",
      };
    case "PICKUP_SITE":
      return {
        value: dispatch.pickupSiteId ?? "NO_PICKUP_SITE",
        displayValue: dispatch.pickupSiteName ?? "No P/U Site",
      };
    case "DELIVERY_SITE":
      return {
        value: dispatch.deliverySiteId ?? "NO_DELIVERY_SITE",
        displayValue: dispatch.deliverySiteName ?? "No Del. Site",
      };
    case "PICKUP_COMPANY":
      return {
        value: dispatch.pickupSiteCompanyId ?? "NO_PICKUP_COMPANY",
        displayValue: dispatch.pickupSiteCompanyName ?? "No P/U Company",
      };
    case "DELIVERY_COMPANY":
      return {
        value: dispatch.deliverySiteCompanyId ?? "NO_DELIVERY_COMPANY",
        displayValue: dispatch.deliverySiteCompanyName ?? "No Del. Company",
      };
    case "SCHEDULED_6_HOUR":
      return getScheduledSixHourBlock(dispatch);
  }
};

export const getDispatchSeparatorValue = (
  dispatch: DispatchEntity | undefined,
  separatorMode: DispatchSeparatorMode
): string | undefined => getDispatchSeparator(dispatch, separatorMode)?.value;

export const getDispatchSeparatorDisplayValue = (
  dispatch: DispatchEntity | undefined,
  separatorMode: DispatchSeparatorMode
): string | undefined => getDispatchSeparator(dispatch, separatorMode)?.displayValue;

type DispatchDataGridRow =
  | {
      type: "SEPARATOR";
      separator: DispatchSeparator;
      shiftContext?: DriverShiftContext;
    }
  | {
      type: "DISPATCH";
      dispatch: DispatchEntity;
    };

const isDispatchRow = (
  row: DispatchDataGridRow | undefined
): row is Extract<DispatchDataGridRow, {type: "DISPATCH"}> => {
  return row?.type === "DISPATCH";
};

const getDispatchFromRow = (row: DispatchDataGridRow | undefined): DispatchEntity | undefined => {
  return isDispatchRow(row) ? row.dispatch : undefined;
};

const getShiftContextDisplayValue = (
  separator: DispatchSeparator,
  separatorMode: DispatchSeparatorMode,
  shifts: ShiftEntity[],
  appDttm?: number
): DriverShiftContext | undefined => {
  if (separatorMode !== "DRIVER" || separator.value === "UNASSIGNED") {
    return undefined;
  }

  return getDriverShiftContext(separator.value, shifts, appDttm);
};

const getDisplayRows = (
  data: DispatchEntity[],
  separatorMode: DispatchSeparatorMode,
  shifts: ShiftEntity[],
  appDttm?: number
): DispatchDataGridRow[] => {
  return getDataGridDisplayRows(
    data,
    separatorMode === "NONE"
      ? undefined
      : (d) => {
          const separator = getDispatchSeparator(d, separatorMode) ?? {value: "UNKNOWN", displayValue: "Unknown"};
          return {...separator, sortValue: separatorMode === "SCHEDULED_6_HOUR" ? separator.value : undefined};
        }
  ).map((row) =>
    row.type === "DATA"
      ? {type: "DISPATCH", dispatch: row.value}
      : {
          type: "SEPARATOR",
          separator: row.separator,
          shiftContext: getShiftContextDisplayValue(row.separator, separatorMode, shifts, appDttm),
        }
  );
};

export const DispatchDataGrid = forwardRef<DataEditorRef, DispatchDataGridProps>((props, ref) => {
  const {
    data = EMPTY_ARRAY,
    gridSelection,
    onItemClick,
    onCellClicked,
    colWidths,
    onColumnResize,
    sort,
    onSort,
    updateDispatches,
    patchDispatch,
    separatorMode: controlledSeparatorMode,
    onSeparatorModeChange,
    controlsTarget,
    columnStorageKey = "ots.dispatch-grid.columns.v1",
    height = "100%",
    onGridSelectionChange,
    shifts = EMPTY_SHIFT_ARRAY,
    appDttm,
    focusPersonId,
    onFocusResolved,
    onCommentCreated,
    ...rest
  } = props;

  const [selectedDispatchId, setSelectedDispatchId] = useState<string | undefined>(undefined);

  const [contextMenu, setContextMenu] = useState<GridRowContextMenu<DispatchEntity>>();

  const mantineTheme = useMantineTheme();
  const inheritedScheme = useOtsColorScheme();
  const colorScheme = inheritedScheme ?? mantineTheme.colorScheme;
  const separatorTheme = useMemo(() => getDataGridSeparatorTheme(colorScheme), [colorScheme]);
  const auth = useAuth();
  const [commentTarget, setCommentTarget] = useState<EntityCommentTarget>();
  const [localSeparatorMode, setLocalSeparatorMode] = useState<DispatchSeparatorMode>("NONE");
  const separatorMode = controlledSeparatorMode ?? localSeparatorMode;
  const setSeparatorMode = (mode: DispatchSeparatorMode) => {
    setLocalSeparatorMode(mode);
    onSeparatorModeChange?.(mode);
  };
  const {colsMap: catalog} = useDispatchDataGridCols({colWidths});
  const initialSort = useRef<ColumnSort | undefined>(
    sort && LEGACY_DISPATCH_COLUMN_IDS[sort.col]
      ? {id: LEGACY_DISPATCH_COLUMN_IDS[sort.col], direction: sort.direction}
      : undefined
  );
  const columnLayout = useDataGridColumnLayout(catalog, columnStorageKey, {defaultSort: initialSort.current});
  const dispatchColsMap = columnLayout.visibleColumns;
  const activeSort = columnLayout.activeSort;
  const sortColumn = dispatchColsMap.find((column) => column.id === activeSort?.id);
  const displayRows = useMemo(() => {
    const ordered =
      activeSort && sortColumn?.getSortValue
        ? getColumnSortedIndices(data, sortColumn.getSortValue, activeSort.direction).map((index) => data[index])
        : data;
    return getDisplayRows(ordered, separatorMode, shifts, appDttm);
  }, [data, activeSort, sortColumn, separatorMode, shifts, appDttm]);

  const colsMap = useMemo<GridColumnWithContent<DispatchDataGridRow>[]>(() => {
    return dispatchColsMap.map((col, index) => ({
      ...col,
      hasMenu: col.sortable !== false,
      getSortValue: col.getSortValue
        ? (row) => (row.type === "DISPATCH" ? col.getSortValue?.(row.dispatch) : undefined)
        : undefined,
      getContent: (row) => {
        if (!row) {
          return {
            kind: GridCellKind.Protected,
            allowOverlay: false,
          };
        }

        if (row.type === "SEPARATOR") {
          const displayData = [
            row.separator.displayValue,
            row.shiftContext?.status,
            row.shiftContext?.equipment,
            row.shiftContext?.shiftWindow,
          ]
            .filter(Boolean)
            .join(" | ");

          if (separatorMode === "DRIVER" && index === 0) {
            return {
              kind: GridCellKind.Custom,
              data: {
                kind: "dispatch-separator-cell",
                title: row.separator.displayValue,
                shiftStatus: row.shiftContext?.status,
                equipment: row.shiftContext?.equipment,
                shiftWindow: row.shiftContext?.shiftWindow,
                colorScheme,
              },
              copyData: displayData,
              allowOverlay: false,
              span: [0, dispatchColsMap.length - 1],
              themeOverride: separatorTheme,
            };
          }

          return {
            kind: GridCellKind.Text,
            data: index === 0 ? displayData : "",
            displayData: index === 0 ? displayData : "",
            allowOverlay: false,
            span: [0, dispatchColsMap.length - 1],
            themeOverride: separatorTheme,
          };
        }

        return col.getContent(row.dispatch, index);
      },
    }));
  }, [dispatchColsMap, separatorMode, separatorTheme, colorScheme]);

  const dataGridArgs = useDataGrid<DispatchDataGridRow>({
    data: displayRows,
    colsMap: colsMap,
    disableSorting: true,
    onColumnResize: columnLayout.onColumnResize,
  });

  const {hoverRow, getOriginalIndex} = dataGridArgs;

  const focusedRecordAt = useCallback(
    (row: number) => getDispatchFromRow(displayRows[getOriginalIndex(row)]),
    [displayRows, getOriginalIndex]
  );
  const matchesFocusedDriver = useCallback(
    (record: DispatchEntity) => record.personId === focusPersonId,
    [focusPersonId]
  );
  const focusedRows = useDataGridRowFocus({
    rowCount: dataGridArgs.rows,
    recordAt: focusedRecordAt,
    matches: matchesFocusedDriver,
    focusKey: focusPersonId,
    onResolved: onFocusResolved,
  });
  const focusTheme = useMemo(() => getDataGridFocusTheme(colorScheme), [colorScheme]);

  useEffect(() => {
    if (onColumnResize) {
      const widths = LEGACY_DISPATCH_COLUMN_IDS.map((id) => {
        const c = columnLayout.columns.find((column) => column.id === id) ?? catalog.find((column) => column.id === id);
        if (!c) return undefined;
        if (isSizedGridColumn(c)) {
          return c.width;
        } else {
          return undefined;
        }
      });
      onColumnResize(widths);
    }
  }, [columnLayout.columns, catalog, onColumnResize]);

  const layoutKey = JSON.stringify([
    dataGridArgs.columns.map((c) => c.id),
    displayRows.map((row) => (row.type === "DISPATCH" ? row.dispatch.id : `separator:${row.separator.value}`)),
  ]);
  const previousLayout = useRef(layoutKey);
  useEffect(() => {
    if (previousLayout.current !== layoutKey) {
      onGridSelectionChange?.({columns: CompactSelection.empty(), rows: CompactSelection.empty()});
      setContextMenu(undefined);
    }
    previousLayout.current = layoutKey;
  }, [layoutKey, onGridSelectionChange]);
  const handleSelectionChange = useCallback<NonNullable<DataEditorProps["onGridSelectionChange"]>>(
    (selection) =>
      onGridSelectionChange?.(
        filterDataGridRecordSelection(selection, (row) => !!getDispatchFromRow(displayRows[getOriginalIndex(row)]))
      ),
    [onGridSelectionChange, displayRows, getOriginalIndex]
  );
  const isOpen = typeof contextMenu !== "undefined";

  const {layerProps, renderLayer} = useLayer({
    isOpen: isOpen,
    auto: true,
    placement: "bottom-end",
    triggerOffset: 2,
    onOutsideClick: () => setContextMenu(undefined),
    layerDimensions: (layerSide) => ({width: 200, height: contextMenu?.possibleHeight ?? 200}),
    trigger: {
      getBounds: () => ({
        left: contextMenu?.bounds.x ?? 0,
        top: contextMenu?.bounds.y ?? 0,
        width: contextMenu?.bounds.width ?? 0,
        height: contextMenu?.bounds.height ?? 0,
        right: (contextMenu?.bounds.x ?? 0) + (contextMenu?.bounds.width ?? 0),
        bottom: (contextMenu?.bounds.y ?? 0) + (contextMenu?.bounds.height ?? 0),
      }),
    },
  });

  const onCellContextMenu = useCallback(
    (cell: GridItem, event: CellClickedEventArgs) => {
      event.preventDefault();

      const rootRow = getOriginalIndex(cell[1]);
      if (!getDispatchFromRow(displayRows[rootRow])) return;

      if (gridSelection && gridSelection.rows.toArray().includes(cell[1])) {
        const selectedDispatches = gridSelection.rows
          .toArray()
          .map((row) => getDispatchFromRow(displayRows[getOriginalIndex(row)]))
          .filter((dispatch): dispatch is DispatchEntity => typeof dispatch !== "undefined");

        if (selectedDispatches.length === 0) {
          return;
        }

        setContextMenu({
          col: cell[0],
          row: rootRow,
          bounds: event.bounds,
          data: selectedDispatches,
          possibleHeight: 200,
        });
      } else {
        const dispatch = getDispatchFromRow(displayRows[rootRow]);
        if (!dispatch) {
          return;
        }

        setContextMenu({
          col: cell[0],
          row: rootRow,
          bounds: event.bounds,
          data: [dispatch],
          possibleHeight: 200,
        });
      }
    },
    [displayRows, getOriginalIndex, gridSelection]
  );

  const onContextMenuItemClick = useCallback(
    (item: DispatchDataGridContextMenuItem, data: DispatchEntity[]) => {
      setContextMenu(undefined);
      if (item === "COMMENT" && data.length === 1 && auth?.hasAccess("dispatch_edit")) {
        setCommentTarget({
          objectId: data[0].id,
          objectType: "DISPATCH",
          label: `dispatch ${data[0].workOrderNumber ?? data[0].id}`,
        });
      } else onItemClick?.(item, data);
    },
    [onItemClick, auth]
  );

  const getRowThemeOverride = useCallback(
    (row: number): Partial<Theme> | undefined => {
      const rowData = displayRows[getOriginalIndex(row)];
      if (rowData?.type === "SEPARATOR") return separatorTheme;
      if (focusedRows.has(row)) return focusTheme;
      const record = getDispatchFromRow(rowData);
      return getDispatchDataGridRowTheme(record, colorScheme, hoverRow === row);
    },
    [displayRows, hoverRow, getOriginalIndex, focusedRows, focusTheme, separatorTheme, colorScheme]
  );

  const highlights = useMemo<DataEditorProps["highlightRegions"]>(() => {
    if (gridSelection?.current === undefined) {
      return undefined;
    }
    const [, row] = gridSelection.current.cell;
    const dispatch = getDispatchFromRow(displayRows[getOriginalIndex(row)]);
    if (dispatch && dispatch.splitGroupId) {
      const highlights: Highlight[] = [];

      displayRows.forEach((value, index) => {
        const rowDispatch = getDispatchFromRow(displayRows[getOriginalIndex(index)]);
        if (rowDispatch?.splitGroupId === dispatch.splitGroupId) {
          highlights.push({
            color: "#22A7F030",
            range: {
              x: 0,
              y: index,
              width: 1,
              height: 1,
            },
            style: "dashed",
          });
        }
      });

      return highlights;
    }

    return undefined;
  }, [gridSelection, displayRows, getOriginalIndex]);

  const _onCellClicked = useCallback(
    (cell: Item, event: CellClickedEventArgs) => {
      if (dataGridArgs.columns[cell[0]]?.id === "INDICATORS") {
        const dispatch = getDispatchFromRow(displayRows[getOriginalIndex(cell[1])]);
        if (dispatch && dispatch.message) {
          setSelectedDispatchId(dispatch.id);
        }
      }

      if (onCellClicked) {
        onCellClicked(cell, event);
      }
    },
    [onCellClicked, displayRows, getOriginalIndex, dataGridArgs.columns]
  );

  const onCellEdited = useCallback<NonNullable<DataEditorProps["onCellEdited"]>>(
    (cell, newValue) => {
      if (dataGridArgs.columns[cell[0]]?.id !== "PERFORM_ORDER" || !patchDispatch) {
        return;
      }

      const dispatch = getDispatchFromRow(displayRows[getOriginalIndex(cell[1])]);
      if (!dispatch) {
        return;
      }

      const performOrder =
        newValue.kind === GridCellKind.Number && typeof newValue.data === "number"
          ? Math.trunc(newValue.data)
          : undefined;

      if (performOrder === dispatch.performOrder) {
        return;
      }

      patchDispatch(
        dispatch.id,
        {
          performOrder: typeof performOrder === "number" ? {present: true, value: performOrder} : {present: true},
        },
        undefined
      );
    },
    [displayRows, getOriginalIndex, patchDispatch, dataGridArgs.columns]
  );

  const nativeSort = useMemo(
    () =>
      activeSort && sortColumn && LEGACY_DISPATCH_COLUMN_IDS.some((id) => id === activeSort.id)
        ? {col: LEGACY_DISPATCH_COLUMN_IDS.findIndex((id) => id === activeSort.id), direction: activeSort.direction}
        : undefined,
    [activeSort, sortColumn]
  );
  useEffect(() => {
    onSort?.(nativeSort);
  }, [onSort, nativeSort]);
  const {toggleSort} = columnLayout;
  const headerMenu = useCallback<NonNullable<DataEditorProps["onHeaderMenuClick"]>>(
    (index) => {
      const column = dispatchColsMap[index];
      if (column?.sortable !== false && column?.id) toggleSort(column.id, "desc");
    },
    [dispatchColsMap, toggleSort]
  );
  const headerClick = useCallback<NonNullable<DataEditorProps["onHeaderClicked"]>>(
    (index, event) => {
      if (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.isEdge)
        headerMenu(index, event.bounds);
    },
    [headerMenu]
  );
  const headerIcons = useMemo(() => getDataGridSortIcons(colorScheme), [colorScheme]);
  const columns = useMemo(
    () =>
      dataGridArgs.columns.map((column) => ({
        ...column,
        icon: activeSort?.id === column.id ? `ots-sort-${activeSort.direction}` : column.icon,
      })),
    [dataGridArgs.columns, activeSort]
  );
  const controls = (
    <Group position="right" mb={controlsTarget === undefined ? "xs" : 0}>
      <DataGridSeparatorPicker mode={separatorMode} onChange={setSeparatorMode} options={DISPATCH_SEPARATOR_OPTIONS} />
      <DataGridColumnPicker {...columnLayout} />
    </Group>
  );

  return (
    <>
      <div style={{display: "flex", flexDirection: "column", height, minHeight: 0, minWidth: 0}}>
        {controlsTarget === undefined ? controls : controlsTarget ? createPortal(controls, controlsTarget) : null}
        <div style={{flex: 1, minHeight: 0}}>
          <ThemedDataGrid
            colorScheme={colorScheme}
            height="100%"
            {...rest}
            {...dataGridArgs}
            ref={ref}
            width="100%"
            smoothScrollX={true}
            smoothScrollY={true}
            rowMarkers="both"
            getCellsForSelection={true}
            columns={columns}
            headerIcons={headerIcons}
            onColumnMoved={columnLayout.onColumnMoved}
            onHeaderMenuClick={headerMenu}
            onHeaderClicked={headerClick}
            onCellClicked={_onCellClicked}
            onCellEdited={onCellEdited}
            getRowThemeOverride={getRowThemeOverride}
            onCellContextMenu={onCellContextMenu}
            onGridSelectionChange={handleSelectionChange}
            gridSelection={gridSelection}
            highlightRegions={highlights}
            rowSelect="multi"
            rowSelectionMode="multi"
          />
        </div>
      </div>
      {commentTarget && (
        <EntityCommentDialog
          key={`${commentTarget.objectType}:${commentTarget.objectId}`}
          target={commentTarget}
          onCreated={onCommentCreated}
          onClose={() => setCommentTarget(undefined)}
        />
      )}
      {isOpen &&
        renderLayer(
          <div {...layerProps}>
            <DispatchDataGridContextMenu data={contextMenu.data} onItemClick={onContextMenuItemClick} />
          </div>
        )}

      {selectedDispatchId && (
        <EntityCommentListModal
          listProps={{
            objectId: selectedDispatchId,
          }}
          withinPortal={true}
          centered={true}
          mih={400}
          opened={true}
          onClose={() => setSelectedDispatchId(undefined)}
        />
      )}
    </>
  );
});
